/* Atlas v3 — shared shell: the bottom market-tape (TickerStrip) + the
   v3 BottomNav. The BottomNav navigates the real app via v2Navigate.

   Ported from the mockup's window-global TickerStrip / BottomNav /
   AtlasElasticTabBar. The elastic indicator is a CSS-transition slide
   (the mockup used gsap; we have no gsap dependency and aren't adding one
   per the no-new-deps rule — a cubic-bezier transition reads the same on
   a 5-tab bar). */
import { useState, useEffect, useRef } from 'react'
import { useV2Path, v2Navigate } from '@/v2/lib/useV2Path'
import { useTicker, marketState } from './lib'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { AtlasSearch } from './Search'
import { FeedbackWidget } from '@/v2/components/FeedbackWidget'

/* ── ticker strip — 28 names (movers + volume + ETFs + crypto) ──
   Price + change% are fed live from useLivePrices (the equity/ETF price hook).
   Symbols it doesn't cover (e.g. VIX, BTC) render "—" until a dedicated
   index/crypto quote source exists.

   This list USED to carry seeded p/c values that the chips fell back to when
   the feed missed a symbol. That made a dead feed indistinguishable from a
   live one — the tape happily showed June's numbers forever. Never reintroduce
   a hardcoded price fallback here; "—" is the honest state. */
const STRIP_TICKERS = [
  'NVDA', 'AGX', 'INOD', 'MU', 'AVGO', 'OKLO', 'KTOS', 'TSLA',
  'AMD', 'PLTR', 'SMCI', 'GEV', 'AAPL', 'MRVL', 'INTC', 'VST',
  'SMR', 'LUNR', 'RCAT', 'POWL', 'SPY', 'QQQ', 'IWM', 'GLD',
  'UMAC', 'BWXT',
  // VIX/BTC removed 2026-08: /api/prices can't quote the index, and Polygon
  // now resolves equity ticker "BTC" to the Grayscale mini-trust ETP — a
  // wrong-instrument price. Re-add only with a dedicated index/crypto source.
]

const MONO = { fontFamily: 'var(--studio-font-mono)' }

/* ── freshness stamp — answers "is this thing even fetching?" ──
   Dot color = market phase (marketState, same helper the Overview header
   uses); the clock = when /api/prices last came back. A red dot + STALE means
   every batch of the last tick failed. */
function Freshness({ updatedAt, error }) {
  const [ms, setMs] = useState(() => marketState())
  useEffect(() => { const id = setInterval(() => setMs(marketState()), 30000); return () => clearInterval(id) }, [])
  const bad = !!error
  const dot = bad ? '--studio-danger' : ms.dot
  const label = bad
    ? 'STALE'
    : updatedAt
      ? new Date(updatedAt).toLocaleTimeString('en-US', { hour12: false })
      : '···'
  return (
    <div title={bad ? 'Last price fetch failed' : ms.label}
      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '0 12px 0 10px', flexShrink: 0 }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: `hsl(var(${dot}))`, flexShrink: 0, display: 'inline-block' }} />
      <span style={{ ...MONO, fontSize: 9.5, letterSpacing: '0.04em', fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--studio-text-subtle))' }}>{label}</span>
    </div>
  )
}

/* ── SearchPill — the global search affordance: a 34px round button that
   lives in the TOP-RIGHT of every v3 screen's page header, plus the overlay
   it opens.

   Placement + styling come straight from the design reference
   (docs/design/v3-handoff/v3/atlas-home.jsx Header, ~L366): a circle on
   --studio-surface-2 with a --studio-border ring, sitting immediately left of
   the "B" avatar on Overview.

   MOUNT POINT IS LOAD-BEARING — do not move this into TickerStrip or any
   other backdrop-filtered container. AtlasSearch is `position: fixed; inset: 0`,
   and an ancestor with backdrop-filter/filter/transform becomes the containing
   block for fixed descendants. PR #814 mounted it inside the tape band (whose
   wrapper is backdrop-filter: blur(12px)), which collapsed the overlay into
   that 38px strip and made search unusable on every screen. Page headers carry
   no filter, and .v3-root is clean, so `inset: 0` resolves to the viewport here.

   Each screen renders its own <SearchPill /> — it pulls openTicker from
   TickerCtx, so there is nothing to thread through props. ── */
export function SearchPill() {
  const openTicker = useTicker()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Search tickers"
        style={{ width: 34, height: 34, flexShrink: 0, borderRadius: '50%', border: '1px solid hsl(var(--studio-border))', background: 'hsl(var(--studio-surface-2))', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--studio-text-muted))" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
      </button>
      <AtlasSearch open={open} onClose={() => setOpen(false)} openTicker={openTicker} />
    </>
  )
}

/* ── TickerStrip — the shared bottom band: scrolling market tape · freshness
   stamp. Search used to sit on its left edge (PR #814); it moved to the page
   header as <SearchPill /> in the header of every screen, which both matches
   the design reference and gives the tape its full width back. ── */
export function TickerStrip() {
  const openTicker = useTicker()
  const { prices, error, updatedAt } = useLivePrices(STRIP_TICKERS)
  const chips = [...STRIP_TICKERS, ...STRIP_TICKERS]
  const trackRef = useRef(null)
  // Same compositor hit-test desync as the Overview marquee: a native tap on a
  // moving chip opens whichever ticker sits at that screen-x in the un-animated
  // layout. Hit-test against the live getBoundingClientRect so the tap opens the
  // chip the user actually sees.
  const pickChip = (e) => {
    const track = trackRef.current
    if (!track) return
    for (const el of track.children) {
      const r = el.getBoundingClientRect()
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
        if (el.dataset.ticker) openTicker(el.dataset.ticker)
        return
      }
    }
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: 38, background: 'transparent' }}>
      {/* the tape's tap handler lives on this wrapper, not on each chip row —
          the chips are mid-animation, so hit-testing has to go through pickChip */}
      <div onClick={pickChip} style={{ flex: 1, minWidth: 0, overflow: 'hidden', height: '100%' }}>
        <div ref={trackRef} style={{ display: 'flex', animation: 'atlasTicker 48s linear infinite', width: 'max-content', height: '100%', alignItems: 'center' }}>
          {chips.map((t, i) => {
            const live = prices[t]
            const c = typeof live?.change1d === 'number' ? live.change1d : null
            const px = typeof live?.price === 'number' ? live.price : null
            const up = (c ?? 0) >= 0
            return (
              <div key={i} data-ticker={t}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px', height: '100%', border: 'none', background: 'transparent', cursor: 'pointer', flexShrink: 0 }}>
                <span style={{ ...MONO, fontSize: 10.5, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-muted))' }}>{t}</span>
                <span style={{ ...MONO, fontSize: 10.5, fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--studio-text))' }}>
                  {px == null ? '—' : px.toFixed(2)}
                </span>
                <span style={{
                  ...MONO, fontSize: 10.5, fontVariantNumeric: 'tabular-nums',
                  color: c == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${up ? '--studio-success' : '--studio-danger'}))`,
                }}>
                  {c == null ? '—' : `${up ? '▲' : '▼'} ${Math.abs(c).toFixed(2)}%`}
                </span>
              </div>
            )
          })}
        </div>
      </div>
      <Freshness updatedAt={updatedAt} error={error} />
    </div>
  )
}

/* ── DeskSwitcher — a route-backed eyebrow strip shared by screen clusters.
   Rendered as the header eyebrow on each screen in a cluster (replacing its
   static kicker); navigates the real routes so every screen keeps its own
   data lifecycle and StockDetailProvider. Default items are the Markets-tab
   desks; the My Stocks cluster passes MY_STOCKS_DESKS. ── */
const DESKS = [
  { id: 'universe', label: 'UNIVERSE', route: '/markets', match: ['/markets', '/discover'] },
  { id: 'leverage', label: 'LEVERAGE', route: '/leverage', match: ['/leverage'] },
  { id: 'global', label: 'GLOBAL', route: '/global', match: ['/global'] },
]
// The My Stocks-tab cluster: watchlist · agent mirror · manual accounts.
export const MY_STOCKS_DESKS = [
  { id: 'watchlist', label: 'WATCHLIST', route: '/my-stocks', match: ['/my-stocks', '/watchlist'] },
  { id: 'agent', label: 'MY AGENT', route: '/agent', match: ['/agent'] },
  { id: 'accounts', label: 'ACCOUNTS', route: '/accounts', match: ['/accounts', '/my-portfolio'] },
]
export function DeskSwitcher({ items = DESKS, variant = 'eyebrow' }) {
  const path = useV2Path()
  const activeId = (items.find((d) => d.match.some((m) => path.startsWith(m) || path.startsWith('/v2' + m))) || items[0]).id

  // 'tabs' — the My Stocks cluster's full-width segmented row: sits BELOW the
  // screen title, equal thirds, 44px touch targets, hairline base, active =
  // bright text + 2px amber underline (amber as a line, per the system).
  // The Markets desks keep the compact 'eyebrow' presentation.
  if (variant === 'tabs') {
    return (
      <div style={{ display: 'flex', borderBottom: '1px solid hsl(var(--studio-border) / 0.5)' }}>
        {items.map((d) => {
          const on = d.id === activeId
          return (
            <button key={d.id} onClick={() => !on && v2Navigate(d.route)} style={{
              flex: 1, height: 44, background: 'transparent', border: 'none',
              borderBottom: on ? '2px solid hsl(var(--studio-accent))' : '2px solid transparent',
              fontFamily: 'var(--studio-font-mono)', fontSize: 11, letterSpacing: '0.1em',
              cursor: on ? 'default' : 'pointer',
              color: on ? 'hsl(var(--studio-text))' : 'hsl(var(--studio-text) / 0.55)',
              transition: 'color 150ms var(--studio-ease)',
            }}>{d.label}</button>
          )
        })}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: 14, marginBottom: 5 }}>
      {items.map((d) => {
        const on = d.id === activeId
        return (
          <button key={d.id} onClick={() => !on && v2Navigate(d.route)} style={{
            fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.18em',
            padding: '0 0 3px', background: 'transparent', border: 'none',
            borderBottom: on ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent',
            cursor: on ? 'default' : 'pointer',
            color: on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))',
            transition: 'color 150ms var(--studio-ease)',
          }}>{d.label}</button>
        )
      })}
    </div>
  )
}

/* ── BottomNav — elastic tab bar wired to the real path router ──
   route: the real app path each tab navigates to. id: matched against
   the current pathname so the active tab highlights correctly. */
// Icon paths are Tabler Icons outline (MIT, tabler.io/icons) — home, wave-sine,
// chart-bar, star, layout-grid — multi-path SVGs merged into single d strings.
// Drawn for a 24-grid at 2px stroke; keep strokeWidth 2 when rendering these.
const NAV = [
  { id: 'home', label: 'Overview', route: '/', d: 'M5 12l-2 0l9 -9l9 9l-2 0M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6' },
  { id: 'cycle', label: 'Cycle', route: '/cycle', d: 'M21 12h-2c-.894 0 -1.662 -.857 -1.761 -2c-.296 -3.45 -.749 -6 -2.749 -6s-2.5 3.582 -2.5 8s-.5 8 -2.5 8s-2.452 -2.547 -2.749 -6c-.1 -1.147 -.867 -2 -1.763 -2h-2' },
  { id: 'markets', label: 'Markets', route: '/markets', d: 'M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -6M15 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -10M9 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -14M4 20h14' },
  { id: 'watch', label: 'My Stocks', route: '/my-stocks', d: 'M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873l-6.158 -3.245' },
  { id: 'more', label: 'More', route: '/more', d: 'M4 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4M14 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4M4 15a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4M14 15a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4' },
]

function activeIdForPath(path) {
  if (path === '/' || path.startsWith('/v2') === false && path === '') return 'home'
  if (path.startsWith('/cycle') || path.startsWith('/v2/cycle')) return 'cycle'
  // All desk routes light the Markets tab: /discover renders the consolidated
  // Markets screen, /leverage + /global are its sibling desks (DeskSwitcher).
  if (path.startsWith('/markets') || path.startsWith('/v2/markets')
    || path.startsWith('/discover') || path.startsWith('/v2/discover')
    || path.startsWith('/leverage') || path.startsWith('/v2/leverage')
    || path.startsWith('/global') || path.startsWith('/v2/global')) return 'markets'
  // The My Stocks cluster (watchlist / agent mirror / manual accounts) all
  // light the My Stocks tab — three routed screens, one DeskSwitcher eyebrow.
  if (path.startsWith('/my-stocks') || path.startsWith('/v2/my-stocks') || path.startsWith('/watchlist')
    || path.startsWith('/agent') || path.startsWith('/v2/agent')
    || path.startsWith('/accounts') || path.startsWith('/v2/accounts')
    || path.startsWith('/my-portfolio') || path.startsWith('/v2/my-portfolio')) return 'watch'
  // Routes reached *from* More (no dedicated tab) keep More lit so the nav
  // never falls back to wrongly highlighting Overview.
  if (path.startsWith('/more') || path.startsWith('/v2/more')
    || path.startsWith('/insights') || path.startsWith('/v2/insights')
    || path.startsWith('/signals') || path.startsWith('/v2/signals')
    || path.startsWith('/settings') || path.startsWith('/v2/settings')
    || path.startsWith('/calendar') || path.startsWith('/v2/calendar')) return 'more'
  return 'home'
}

/* ── More — bottom-sheet overlay (slides up over the current screen, dims
   behind, amber top hairline, grabber + ✕). Opened by the More tab below
   instead of routing to a full /more page (per the handoff MoreSheet). Rows
   navigate to the real routes and close the sheet. */
// Tabler outline paths (calendar, book, bolt, settings), same merged-d format as NAV.
const MORE_ITEMS = [
  { id: 'calendar', label: 'Calendar', sub: 'Earnings & catalysts', route: '/calendar', d: 'M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12M16 3v4M8 3v4M4 11h16M11 15h1M12 15v3' },
  { id: 'insights', label: 'Insights', sub: 'The Weekly', route: '/insights', d: 'M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0M3 6l0 13M12 6l0 13M21 6l0 13' },
  { id: 'signals', label: 'Signals', sub: 'Live signal feed', route: '/signals', d: 'M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11' },
  { id: 'settings', label: 'Settings', sub: 'Alerts, account & appearance', route: '/settings', d: 'M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0' },
]
const MORE_SOON = ['Lab', 'Reports', 'AI Universe', 'Capital Flow']

function MoreRow({ item, onNavigate }) {
  const [hot, setHot] = useState(false)
  return (
    <button onClick={() => onNavigate(item.route)}
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 13, width: '100%', padding: '13px 14px', textAlign: 'left', cursor: 'pointer',
        background: hot ? 'hsl(var(--studio-surface-2) / 0.7)' : 'hsl(var(--studio-surface) / 0.5)', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius)', transition: 'background 160ms' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={hot ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-muted))'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: 'stroke 160ms' }}><path d={item.d} /></svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--studio-font-sans)', fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{item.label}</div>
        <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>{item.sub}</div>
      </div>
      <span style={{ color: 'hsl(var(--studio-accent-soft))', fontSize: 15 }}>›</span>
    </button>
  )
}

function MoreSheet({ onClose }) {
  const [shown, setShown] = useState(false)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  function go(route) { onClose(); v2Navigate(route) }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 64 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '82%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.5)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden' }}>
        <div style={{ padding: '9px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <span onClick={onClose} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 24, color: 'hsl(var(--studio-text))' }}>More</span>
            <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
          </div>
        </div>
        <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 28px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MORE_ITEMS.map((it) => <MoreRow key={it.id} item={it} onNavigate={go} />)}
          </div>
          <div style={{ height: 1, background: 'hsl(var(--studio-border) / 0.6)', margin: '18px 2px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MORE_SOON.map((label) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 14px', background: 'hsl(var(--studio-surface) / 0.3)', border: '1px solid hsl(var(--studio-border) / 0.5)', borderRadius: 'var(--studio-radius)', opacity: 0.5 }}>
                <span style={{ fontFamily: 'var(--studio-font-sans)', fontSize: 13.5, color: 'hsl(var(--studio-text-muted))' }}>{label}</span>
                <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>COMING SOON</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function BottomNav() {
  const path = useV2Path()
  const [moreOpen, setMoreOpen] = useState(false)
  const activeId = moreOpen ? 'more' : activeIdForPath(path)
  const containerRef = useRef(null)
  const indicatorRef = useRef(null)

  // Lock the document while a v3 screen is mounted (BottomNav ships with every
  // v3 screen). v3 scrolls only inside its own .atlas-scroll containers, so
  // any window-level scroll is iOS elastic rubber-band — a hard drag yanked
  // the whole shell down leaving a black void above it. position:fixed body
  // (body.v3-lock in v3.css) is the reliable iOS suppressor; class-scoped so
  // /v1 on phones and the desktop app keep their window scroll.
  useEffect(() => {
    document.body.classList.add('v3-lock')
    return () => document.body.classList.remove('v3-lock')
  }, [])

  // Slide the amber indicator under the active tab (CSS transition, no gsap).
  useEffect(() => {
    const container = containerRef.current
    const indicator = indicatorRef.current
    if (!container || !indicator) return
    const tabEl = container.querySelector(`[data-etab="${activeId}"]`)
    if (!tabEl) return
    const cr = container.getBoundingClientRect()
    const tr = tabEl.getBoundingClientRect()
    const cx = tr.left - cr.left + (tr.width - 24) / 2
    indicator.style.transform = `translateX(${cx}px)`
  }, [activeId, path])

  return (
    <>
    {/* Feedback widget — mounted here because BottomNav ships with every v3
        screen. Its previous (and only) home was the V2 AppLayout, deleted with
        the /picks/:id cluster; without this the in-app feedback_notes intake
        would have died with it. FAB is fixed-position + self-offsets above the
        nav, so it doesn't affect layout. */}
    <FeedbackWidget />
    {moreOpen && <MoreSheet onClose={() => setMoreOpen(false)} />}
    {/* marginTop:auto pins the nav to the flex-column bottom (the root's true
        896 bottom). NOT position:sticky — iOS clamps sticky elements to the
        short 848 layout viewport, which floated the nav 48px above the bottom. */}
    <div style={{ position: 'relative', zIndex: 30, marginTop: 'auto' }}>
      <div style={{ padding: '10px 8px env(safe-area-inset-bottom)', background: 'hsl(var(--studio-background) / 0.86)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderTop: '1px solid hsl(var(--studio-border))' }}>
        <div ref={containerRef} style={{ display: 'flex', position: 'relative' }}>
          <div ref={indicatorRef} style={{
            position: 'absolute', top: 0, left: 0, width: 24, height: 2.5, borderRadius: 999,
            background: 'hsl(var(--studio-accent))', pointerEvents: 'none', zIndex: 2,
            transition: 'transform 0.55s var(--studio-ease)',
          }} />
          {NAV.map((t) => {
            const on = t.id === activeId
            return (
              <button key={t.id} data-etab={t.id} onClick={() => (t.id === 'more' ? setMoreOpen(true) : v2Navigate(t.route))}
                style={{ flex: 1, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '4px 0' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke={on ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-muted))'}
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transition: 'stroke 200ms', transform: on ? 'translateY(-1px)' : 'none' }}>
                  <path d={t.d} />
                </svg>
                <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.06em', color: on ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-muted))', transition: 'color 200ms' }}>{t.label}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
    </>
  )
}
